// Settings storage (localStorage) — PSD §3.5, P1.16. Persistence layer.

import type { StateStorage } from 'zustand/middleware';
import type { SettingsRecord } from './schema';

const SETTINGS_KEY = 'backsolve:settings';
const ACTIVE_KEY = 'backsolve:activeValuationId';

export const defaultSettings: SettingsRecord = {
  activeEntityName: null,
  clientSideProcessing: true,
  rbacRole: 'editor',
  theme: 'light',
  defaultVolatility: 0.5,
  defaultTimeToLiquidity: 5.0,
  showLoadDemo: true,
};

/** localStorage-backed StateStorage for the Zustand persist middleware. */
export const settingsStorage: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      const raw = localStorage.getItem(name);
      return raw ?? null;
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // quota / private mode — swallow; surfaced as toast upstream
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

export function loadSettings(): SettingsRecord {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...defaultSettings };
    const parsed = JSON.parse(raw) as Partial<SettingsRecord>;
    return { ...defaultSettings, ...parsed };
  } catch {
    return { ...defaultSettings };
  }
}

export function saveSettings(settings: SettingsRecord): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore quota
  }
}

export function loadActiveValuationId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

export function saveActiveValuationId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // ignore
  }
}
